import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import {
    useGetAllBankAccountsQuery,
    useCreateBankAccountMutation,
    useUpdateBankAccountMutation,
    useDeleteBankAccountMutation,
    BankAccount,
    CreateBankAccountRequest,
    UpdateBankAccountRequest
} from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import toast from 'react-hot-toast';
import { PlusIcon, Search, RotateCcw, Globe, CreditCard } from 'lucide-react';
import { getData } from 'country-list';

// Type declaration for country data
interface CountryData {
    code: string;
    name: string;
}

// Bank options
const bankOptions = [
    { value: 'Bank Of America', label: 'Bank of America' },
    { value: 'Chase Bank', label: 'Chase Bank' },
    { value: 'Wells Fargo', label: 'Wells Fargo' },
    { value: 'Citibank', label: 'Citibank' },
    { value: 'Goldman Sachs', label: 'Goldman Sachs' },
    { value: 'Morgan Stanley', label: 'Morgan Stanley' },
    { value: 'JPMorgan Chase', label: 'JPMorgan Chase' },
    { value: 'American Express', label: 'American Express' },
    { value: 'Capital One', label: 'Capital One' },
    { value: 'US Bank', label: 'US Bank' },
];

// Country options
const countryOptions = (getData() as CountryData[]).map((c: CountryData) => ({ value: c.code, label: c.name }));

const AdminBankAccounts: React.FC = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);
    const [formData, setFormData] = useState<CreateBankAccountRequest>({
        account_holder_name: '',
        bank_name: '',
        account_number: '',
        country_code: '',
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // API hooks
    const { data: bankAccountsData, isLoading, error } = useGetAllBankAccountsQuery();
    const [createBankAccount, { isLoading: isCreating }] = useCreateBankAccountMutation();
    const [updateBankAccount, { isLoading: isUpdating }] = useUpdateBankAccountMutation();
    const [deleteBankAccount, { isLoading: isDeleting }] = useDeleteBankAccountMutation();

    const bankAccounts = bankAccountsData?.data || [];

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('all');

    const handleResetFilters = () => {
        setSearchTerm('');
        setCountryFilter('all');
    };

    const hasActiveFilters = searchTerm !== '' || countryFilter !== 'all';

    const filteredAccounts = bankAccounts.filter(account => {
        const searchMatch = !searchTerm || 
            account.account_holder_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            account.bank_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            account.account_number.toLowerCase().includes(searchTerm.toLowerCase());
        
        const countryMatch = countryFilter === 'all' || account.country_code === countryFilter;

        return searchMatch && countryMatch;
    });

    const activeCountries = Array.from(new Set(bankAccounts.map(acc => acc.country_code)));
    const activeCountryOptions = [
        { value: 'all', label: 'All Countries' },
        ...activeCountries.map(code => ({
            value: code,
            label: countryOptions.find(c => c.value === code)?.label || code
        }))
    ];

    // Clear form errors when modal is closed
    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
        setFormData({
            account_holder_name: '',
            bank_name: '',
            account_number: '',
            country_code: '',
        });
        setFormErrors({});
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedBankAccount(null);
        setFormData({
            account_holder_name: '',
            bank_name: '',
            account_number: '',
            country_code: '',
        });
        setFormErrors({});
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.account_holder_name.trim()) {
            errors.account_holder_name = 'Account holder name is required';
        }
        if (!formData.bank_name.trim()) {
            errors.bank_name = 'Bank name is required';
        }
        if (!formData.account_number.trim()) {
            errors.account_number = 'Account number is required';
        }
        if (!formData.country_code) {
            errors.country_code = 'Country is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            await createBankAccount(formData).unwrap();
            toast.success('Bank account created successfully!');
            handleCloseCreateModal();
            setFormData({
                account_holder_name: '',
                bank_name: '',
                account_number: '',
                country_code: '',
            });
            setFormErrors({});
        } catch (error) {
            console.error('Failed to create bank account:', error);
            toast.error('Failed to create bank account. Please try again.');
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedBankAccount || !validateForm()) return;

        try {
            const updateData: UpdateBankAccountRequest = {
                bank_account_id: selectedBankAccount.bank_account_id,
                ...formData,
            };
            await updateBankAccount(updateData).unwrap();
            toast.success('Bank account updated successfully!');
            handleCloseEditModal();
            setFormData({
                account_holder_name: '',
                bank_name: '',
                account_number: '',
                country_code: '',
            });
            setFormErrors({});
        } catch (error) {
            console.error('Failed to update bank account:', error);
            toast.error('Failed to update bank account. Please try again.');
        }
    };

    const handleDelete = async () => {
        if (!selectedBankAccount) return;

        try {
            await deleteBankAccount({ bank_account_id: selectedBankAccount.bank_account_id }).unwrap();
            toast.success('Bank account deleted successfully!');
            setIsDeleteModalOpen(false);
            setSelectedBankAccount(null);
        } catch (error) {
            console.error('Failed to delete bank account:', error);
            toast.error('Failed to delete bank account. Please try again.');
        }
    };

    const openEditModal = (bankAccount: BankAccount) => {
        setSelectedBankAccount(bankAccount);
        setFormData({
            account_holder_name: bankAccount.account_holder_name,
            bank_name: bankAccount.bank_name,
            account_number: bankAccount.account_number,
            country_code: bankAccount.country_code,
        });
        setFormErrors({});
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (bankAccount: BankAccount) => {
        setSelectedBankAccount(bankAccount);
        setIsDeleteModalOpen(true);
    };

    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    const maskAccountNumber = (accountNumber: string) => {
        if (!accountNumber || accountNumber.length <= 4) return accountNumber;
        return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Error loading bank accounts</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Bank Accounts</h1>
                    <p className="text-sm text-gray-600">Manage bank account information</p>
                </div>
                <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                >
                    <PlusIcon className="w-4 h-4 mr-2" /> Add Bank Account
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-xs font-medium text-gray-600">Total Accounts</p>
                            <p className="text-lg font-semibold text-gray-900">{bankAccounts.length}</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-xs font-medium text-gray-600">Active</p>
                            <p className="text-lg font-semibold text-gray-900">{bankAccounts.length}</p>
                        </div>
                    </div>
                </Card>

                <Card className="p-4 sm:col-span-2 lg:col-span-1">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-xs font-medium text-gray-600">Countries</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {new Set(bankAccounts.map(acc => acc.country_code)).size}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Premium Filtering Hub */}
            <Card className="border-none shadow-xl bg-white/80 backdrop-blur-md sticky top-0 z-10">
                <div className="flex flex-col space-y-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-1">
                        {/* Search Bar */}
                        <div className="relative flex-1 group">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                            <Input
                                placeholder="Search by holder name, bank, or account number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-12 h-14 bg-gray-50/50 border-gray-200 focus:bg-white transition-all shadow-inner rounded-2xl text-base"
                            />
                        </div>

                        {/* Filter Actions */}
                        <div className="flex items-center gap-4">
                            {hasActiveFilters && (
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={handleResetFilters}
                                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-12 px-6 font-bold text-xs uppercase tracking-widest transition-all shadow-sm"
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Reset
                                </Button>
                            )}
                            <Button
                                onClick={() => setIsCreateModalOpen(true)}
                                className="h-12 px-8 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 font-bold"
                            >
                                <PlusIcon className="w-4 h-4 mr-2" /> Add Account
                            </Button>
                        </div>
                    </div>

                    {/* Filter Selectors Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-2 px-1">
                        <div className="space-y-2">
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                                <Globe className="w-3 h-3 mr-1.5" /> Regional Filter
                            </label>
                            <Select
                                value={countryFilter}
                                onChange={(e) => setCountryFilter(e.target.value)}
                                options={activeCountryOptions}
                                className="h-12 text-sm font-semibold border-gray-200 bg-white hover:border-blue-300 transition-colors rounded-xl shadow-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">
                                <CreditCard className="w-3 h-3 mr-1.5" /> Quick Summary
                            </label>
                            <div className="flex items-center h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl">
                                <span className="text-xs font-bold text-gray-500">{filteredAccounts.length} matching records</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Results Header */}
            <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em]">Financial Nodes</h3>
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 font-bold px-3 py-1 rounded-full">
                        {filteredAccounts.length} Accounts
                    </Badge>
                </div>
            </div>

            {/* Bank Accounts Table */}
            <Card className="p-0 border-none shadow-2xl overflow-hidden bg-white/50 backdrop-blur-sm">
                {filteredAccounts.length === 0 ? (
                    <div className="text-center py-20 bg-white">
                        <div className="text-gray-200 mb-6">
                            <CreditCard className="w-20 h-20 mx-auto" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Matching Accounts</h3>
                        <p className="text-gray-500 mb-8 max-w-xs mx-auto">We couldn't find any bank accounts matching your current filters. Try resetting or adjusting your search.</p>
                        {hasActiveFilters && (
                            <Button onClick={handleResetFilters} variant="outline" size="sm" className="font-bold border-gray-300">
                                Clear All Filters
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table
                            columns={[
                                {
                                    key: 'sno',
                                    label: 'S.No',
                                    render: (_: any, __: any, index: number) => (
                                        <div className="font-bold text-gray-400 text-xs">#{index + 1}</div>
                                    ),
                                },
                                {
                                    key: 'account_holder_name',
                                    label: 'Account Holder',
                                    render: (value) => (
                                        <div className="font-medium text-gray-900 text-sm">{value}</div>
                                    ),
                                },
                                {
                                    key: 'bank_name',
                                    label: 'Bank Name',
                                    render: (value) => <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 border-none">{value}</Badge>,
                                },
                                {
                                    key: 'account_number',
                                    label: 'Account Number',
                                    render: (value) => (
                                        <div className="font-mono text-gray-600 text-sm bg-gray-50 px-2 py-1 rounded inline-block">
                                            {maskAccountNumber(value)}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'country_code',
                                    label: 'Country',
                                    render: (value) => (
                                        <Badge variant="outline" className="text-xs font-bold border-gray-200">
                                            {countryOptions.find((c: { value: string; label: string }) => c.value === value)?.label || value}
                                        </Badge>
                                    ),
                                },
                                {
                                    key: 'created_at',
                                    label: 'Created',
                                    render: (value) => (
                                        <div className="text-xs text-gray-500 font-medium">
                                            {formatDate(value)}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'actions',
                                    label: 'Actions',
                                    render: (_, row) => (
                                        <div className="flex space-x-1">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openEditModal(row)}
                                                className="text-xs px-3 py-1.5 h-8 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => openDeleteModal(row)}
                                                className="text-xs px-3 py-1.5 h-8 bg-red-50 text-red-600 border-red-100 hover:bg-red-100"
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    ),
                                },
                            ]}
                            data={filteredAccounts}
                            itemsPerPage={10}
                        />
                    </div>
                )}
            </Card>

            {/* Create Modal */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={handleCloseCreateModal}
                title="Add Bank Account"
                size="lg"
            >
                <form onSubmit={handleCreateSubmit} className="space-y-4">
                    {/* Mobile: Single column, Tablet: 2 columns, Desktop: 3 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Bank Name *
                            </label>
                            <Input
                                type="text"
                                value={formData.bank_name}
                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                placeholder="Enter bank name"
                                className={formErrors.bank_name ? 'border-red-500' : ''}
                            />
                            {formErrors.bank_name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.bank_name}</p>
                            )}
                        </div>

                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Account Number *
                            </label>
                            <Input
                                type="text"
                                value={formData.account_number}
                                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                                placeholder="Enter account number"
                                className={formErrors.account_number ? 'border-red-500' : ''}
                            />
                            {formErrors.account_number && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.account_number}</p>
                            )}
                        </div>

                        <div className="lg:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Account Holder *
                            </label>
                            <Input
                                type="text"
                                value={formData.account_holder_name}
                                onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                                placeholder="Enter account holder name"
                                className={formErrors.account_holder_name ? 'border-red-500' : ''}
                            />
                            {formErrors.account_holder_name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.account_holder_name}</p>
                            )}
                        </div>

                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Country *
                            </label>
                            <Select
                                value={formData.country_code}
                                onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                                options={countryOptions}
                                placeholder="Select country"
                                className={formErrors.country_code ? 'border-red-500' : ''}
                            />
                            {formErrors.country_code && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.country_code}</p>
                            )}
                        </div>
                    </div>

                    {/* Sticky bottom buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCloseCreateModal}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isCreating}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                        >
                            {isCreating ? 'Creating...' : 'Save Account'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                title="Edit Bank Account"
                size="lg"
            >
                <form onSubmit={handleEditSubmit} className="space-y-4">
                    {/* Mobile: Single column, Tablet: 2 columns, Desktop: 3 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Bank Name *
                            </label>
                            <Input
                                type="text"
                                value={formData.bank_name}
                                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                                placeholder="Enter bank name"
                                className={formErrors.bank_name ? 'border-red-500' : ''}
                            />
                            {formErrors.bank_name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.bank_name}</p>
                            )}
                        </div>

                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Account Number *
                            </label>
                            <Input
                                type="text"
                                value={formData.account_number}
                                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                                placeholder="Enter account number"
                                className={formErrors.account_number ? 'border-red-500' : ''}
                            />
                            {formErrors.account_number && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.account_number}</p>
                            )}
                        </div>

                        <div className="lg:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Account Holder *
                            </label>
                            <Input
                                type="text"
                                value={formData.account_holder_name}
                                onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                                placeholder="Enter account holder name"
                                className={formErrors.account_holder_name ? 'border-red-500' : ''}
                            />
                            {formErrors.account_holder_name && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.account_holder_name}</p>
                            )}
                        </div>

                        <div className="lg:col-span-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Country *
                            </label>
                            <Select
                                value={formData.country_code}
                                onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                                options={countryOptions}
                                placeholder="Select country"
                                className={formErrors.country_code ? 'border-red-500' : ''}
                            />
                            {formErrors.country_code && (
                                <p className="text-xs text-red-500 mt-1">{formErrors.country_code}</p>
                            )}
                        </div>
                    </div>

                    {/* Sticky bottom buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCloseEditModal}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isUpdating}
                            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                        >
                            {isUpdating ? 'Updating...' : 'Update Account'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Delete Bank Account"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Are you sure you want to delete the bank account for{' '}
                        <span className="font-semibold">{selectedBankAccount?.account_holder_name}</span>?
                        This action cannot be undone.
                    </p>

                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="w-full sm:w-auto text-sm px-4 py-2"
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminBankAccounts; 